import React from 'react';
import { X } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { StatusBadge } from './StatusBadge';
import {
  getBranchMeta,
  getStatusMeta,
  getInitials,
  formatDate,
  getEventMeta,
} from '../utils/format';
import styles from './TaskDetail.module.css';

function AgentChip({ agent, onClick }) {
  if (!agent) {
    return (
      <span className={styles.unassigned}>
        Unassigned
      </span>
    );
  }
  const branchMeta = getBranchMeta(agent.branch);
  return (
    <button
      type="button"
      className={styles.agentChip}
      onClick={() => onClick?.(agent.id)}
    >
      <span
        className={styles.chipAvatar}
        style={{
          backgroundColor: `${branchMeta.color}20`,
          color: branchMeta.color,
        }}
      >
        {getInitials(agent.name)}
      </span>
      <span className={styles.chipName}>{agent.name}</span>
    </button>
  );
}

function RaciMatrix({ raci, onAgentClick }) {
  const rows = [
    { key: 'responsible', label: 'Responsible' },
    { key: 'accountable', label: 'Accountable' },
    { key: 'consulted', label: 'Consulted' },
    { key: 'informed', label: 'Informed' },
  ];

  return (
    <div className={styles.raci}>
      <h3 className={styles.sectionTitle}>RACI Matrix</h3>
      {rows.map(({ key, label }) => {
        const agents = raci?.[key] || [];
        return (
          <div key={key} className={styles.raciRow}>
            <span className={styles.raciLabel}>{label}</span>
            <div className={styles.raciAgents}>
              {agents.length > 0 ? (
                agents.map((agent) => (
                  <AgentChip key={agent.id} agent={agent} onClick={onAgentClick} />
                ))
              ) : (
                <AgentChip agent={null} />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DependencyList({ title, tasks, onClick }) {
  if (!tasks?.length) return null;
  return (
    <div className={styles.dependencies}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      <ul className={styles.depList}>
        {tasks.map((task) => {
          const statusMeta = getStatusMeta(task.status);
          return (
            <li key={task.id}>
              <button
                type="button"
                className={styles.depItem}
                onClick={() => onClick?.(task.id)}
              >
                <span className={styles.depDot} style={{ backgroundColor: statusMeta.color }} />
                <span className={styles.depTitle}>#{task.id} {task.title}</span>
                <StatusBadge status={task.status} />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function HistoryTimeline({ history }) {
  const [sort, setSort] = React.useState('desc');
  const sorted = React.useMemo(() => {
    const copy = [...(history || [])];
    copy.sort((a, b) => (sort === 'desc' ? b.timestamp.localeCompare(a.timestamp) : a.timestamp.localeCompare(b.timestamp)));
    return copy;
  }, [history, sort]);

  return (
    <div className={styles.history}>
      <div className={styles.historyHeader}>
        <h3 className={styles.sectionTitle}>History</h3>
        <button
          type="button"
          className={styles.sortButton}
          onClick={() => setSort((s) => (s === 'desc' ? 'asc' : 'desc'))}
        >
          {sort === 'desc' ? 'Newest first' : 'Oldest first'}
        </button>
      </div>

      <ul className={styles.timeline}>
        {sorted.map((event, index) => {
          const meta = getEventMeta(event.event_type);
          const isLast = index === sorted.length - 1;
          return (
            <li key={event.id || index} className={styles.timelineEvent}>
              <div className={styles.timelineConnector}>
                <span className={styles.timelineDot} style={{ backgroundColor: meta.color }} />
                {!isLast && <span className={styles.timelineLine} />}
              </div>
              <div className={styles.timelineContent}>
                <span className={styles.timelineTime}>{formatDate(event.timestamp)}</span>
                <span className={styles.timelineType} style={{ color: meta.color }}>
                  {event.event_type.replace(/_/g, ' ')}
                </span>
                <span className={styles.timelineActor}>{event.actor_role || event.actor}</span>
                <p className={styles.timelineDescription}>{event.description}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function TaskDetail({ taskId, onAgentClick, onTaskClick, onClose }) {
  const { get } = useApi();
  const [task, setTask] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    async function fetchTask() {
      setLoading(true);
      setError(null);
      try {
        const data = await get(`/api/tasks/${taskId}`);
        if (!cancelled) setTask(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchTask();
    return () => {
      cancelled = true;
    };
  }, [taskId, get]);

  if (loading) {
    return (
      <div className={styles.card}>
        <div className="skeleton" style={{ height: 24, width: 200 }} />
        <div className="skeleton" style={{ height: 14, width: 120, marginTop: 12 }} />
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className={styles.card}>
        <div className={styles.error}>{error || 'Task not found'}</div>
      </div>
    );
  }

  const branchMeta = getBranchMeta(task.branch);
  const statusMeta = getStatusMeta(task.status);

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close detail panel"
        >
          <X size={18} />
        </button>
      </div>

      <div className={styles.titleSection}>
        <h2 className={styles.title}>{task.title}</h2>
        <div className={styles.titleMeta}>
          {branchMeta && (
            <span
              className={styles.branchBadge}
              style={{
                backgroundColor: `${branchMeta.color}12`,
                color: branchMeta.color,
              }}
            >
              {task.branch}
            </span>
          )}
          <StatusBadge status={task.status} size="lg" />
        </div>
      </div>

      {task.description && (
        <p className={styles.description}>{task.description}</p>
      )}

      <div className={styles.progressSection}>
        <div className={styles.progressHeader}>
          <span className={styles.sectionTitle}>Progress</span>
          <span className={styles.progressText}>{task.progress ?? 0}%</span>
        </div>
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{
              width: `${task.progress ?? 0}%`,
              backgroundColor: statusMeta.color,
            }}
          />
        </div>
      </div>

      <RaciMatrix raci={task.raci} onAgentClick={onAgentClick} />

      <DependencyList title="Blocked by" tasks={task.dependencies} onClick={onTaskClick} />
      <DependencyList title="Blocks" tasks={task.dependents} onClick={onTaskClick} />

      <HistoryTimeline history={task.history} />
    </div>
  );
}
